import React from 'react';
import { ButtonBase, Paper, Tooltip } from '@mui/material';
import { Add, Remove, Navigation } from '@mui/icons-material';
import { FlyToInterpolator } from '@deck.gl/core';
import { useAtom, useSetAtom } from 'jotai';
import { viewStateAtom, visualModeAtom } from './atoms';

const MIN_ZOOM = 0;
const MAX_ZOOM = 18;

const navButtonSx = {
  backgroundColor: 'white',
  width: 29,
  height: 29,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderBottom: '1px solid rgba(0,0,0,0.1)',
  '&:last-child': { borderBottom: 'none' },
  '&:hover': { backgroundColor: 'rgba(0,0,0,0.05)' },
} as const;

export const NavigationButtons = () => {
  const [viewState, setViewState] = useAtom(viewStateAtom);
  const setVisualMode = useSetAtom(visualModeAtom);

  const handleZoomIn = React.useCallback(() => {
    setViewState(vs => ({
      ...vs,
      zoom: Math.min((vs.zoom ?? 0) + 1, MAX_ZOOM),
      transitionInterpolator: new FlyToInterpolator({ speed: 3.0 }),
      transitionDuration: 300,
    }));
  }, [setViewState]);

  const handleZoomOut = React.useCallback(() => {
    setViewState(vs => ({
      ...vs,
      zoom: Math.max((vs.zoom ?? 0) - 1, MIN_ZOOM),
      transitionInterpolator: new FlyToInterpolator({ speed: 3.0 }),
      transitionDuration: 300,
    }));
  }, [setViewState]);

  const handleCompassReset = React.useCallback(() => {
    setViewState(vs => ({
      ...vs,
      bearing: 0,
      pitch: 0,
      transitionInterpolator: new FlyToInterpolator({ speed: 3.0 }),
      transitionDuration: 500,
    }));
    setVisualMode('2d');
  }, [setViewState, setVisualMode]);

  const bearing = viewState.bearing ?? 0;

  return (
    <Paper
      elevation={0}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        borderRadius: '4px',
        overflow: 'hidden',
        border: '1px solid rgba(0,0,0,0.1)',
      }}
    >
      <Tooltip title="Zoom in" placement="left">
        <ButtonBase sx={navButtonSx} onClick={handleZoomIn} aria-label="Zoom in">
          <Add fontSize="small" />
        </ButtonBase>
      </Tooltip>
      <Tooltip title="Zoom out" placement="left">
        <ButtonBase sx={navButtonSx} onClick={handleZoomOut} aria-label="Zoom out">
          <Remove fontSize="small" />
        </ButtonBase>
      </Tooltip>
      <Tooltip title="Reset bearing" placement="left">
        <ButtonBase sx={navButtonSx} onClick={handleCompassReset} aria-label="Reset bearing">
          <Navigation
            fontSize="small"
            data-testid="compass-icon"
            sx={{ transform: `rotate(${-bearing}deg)`, transition: 'transform 0.3s' }}
          />
        </ButtonBase>
      </Tooltip>
    </Paper>
  );
};
